import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import type { Listing } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { isWholesaleMerchantOnly, canBuyAsCustomer } from "@/lib/userRoles";
import { isStoreServiceCategory, ownProductCartBlock, OWN_PRODUCT_NOT_ORDERABLE_MESSAGE } from "@shared/ownProductCategories";

type AddToCartOpts = {
  isStoreOwnProduct?: boolean;
  storeOwnProductId?: number;
  isStoreDropshippingProduct?: boolean;
  virtualShopOwnerId?: string;
  storeProductId?: number;
};

const MAX_CART_ITEMS = 20;
const MAX_QTY = 99;

interface CartItem {
  listing: Listing;
  quantity: number;
  isStoreOwnProduct?: boolean;
  storeOwnProductId?: number;
  isStoreDropshippingProduct?: boolean;
  virtualShopOwnerId?: string;
  storeProductId?: number;
}

interface StoredCartItem {
  listingId: number;
  quantity: number;
  isStoreOwnProduct?: boolean;
  storeOwnProductId?: number;
  isStoreDropshippingProduct?: boolean;
  virtualShopOwnerId?: string;
  storeProductId?: number;
  savedAt?: number; // unix ms — for automatic expiry
}

interface SellerConflict {
  listing: Listing;
  opts?: AddToCartOpts;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (listing: Listing, opts?: AddToCartOpts) => void;
  sellerConflict: SellerConflict | null;
  resolveConflict: (replace: boolean) => void;
  removeFromCart: (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => void;
  updateQuantity: (
    listingId: number,
    quantity: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => void;
  clearCart: () => void;
  isInCart: (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => boolean;
  getQuantity: (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => number;
  totalItems: number;
  totalPriceIQD: number;
  totalPriceUSD: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const GUEST_CART_KEY = "souq-iraq-cart-guest";

const getCartStorageKey = (userId: number | string | null) => {
  return userId ? `souq-iraq-cart-${userId}` : GUEST_CART_KEY;
};

// Cart items expire after 7 days of inactivity
const CART_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const loadStoredItems = (storageKey: string): StoredCartItem[] => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];

    const toPositiveInt = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
    };

    const now = Date.now();
    const normalized: Array<Omit<StoredCartItem, "listingId"> & { listingId?: number }> = parsed
      // Reject items older than CART_EXPIRY_MS
      .filter((item: StoredCartItem) => !item.savedAt || (now - item.savedAt) < CART_EXPIRY_MS)
      .map((item: StoredCartItem & { listing?: Listing & { listingId?: number | string; storeOwnProductId?: number | string; isStoreOwnProduct?: boolean; storeProductId?: number | string; virtualShopOwnerId?: string; isStoreDropshippingProduct?: boolean } }) => {
        const listingId = toPositiveInt(item.listingId) ?? toPositiveInt(item.listing?.id) ?? toPositiveInt(item.listing?.listingId);
        const ownProductId = toPositiveInt(item.storeOwnProductId) ?? toPositiveInt(item.listing?.storeOwnProductId);
        const dsProductId = toPositiveInt(item.storeProductId) ?? toPositiveInt(item.listing?.storeProductId);
        // Only trust virtualShopOwnerId if the item is explicitly flagged as dropshipping
        const isExplicitlyDS = !!(item.isStoreDropshippingProduct || item.listing?.isStoreDropshippingProduct);
        const virtualShopOwnerId = isExplicitlyDS
          ? (item.virtualShopOwnerId ?? item.listing?.virtualShopOwnerId ?? "").toString().trim() || undefined
          : undefined;

        return {
          listingId,
          quantity: item.quantity,
          isStoreOwnProduct: !!(item.isStoreOwnProduct || item.listing?.isStoreOwnProduct || ownProductId),
          storeOwnProductId: ownProductId,
          // Only set isStoreDropshippingProduct if explicitly flagged — do NOT infer from storeProductId alone
          isStoreDropshippingProduct: isExplicitlyDS,
          virtualShopOwnerId,
          storeProductId: isExplicitlyDS ? dsProductId : undefined,
          savedAt: item.savedAt,
        };
      });

    return normalized
      .filter((item) => typeof item.listingId === "number")
      .map((item) => ({ ...item, listingId: item.listingId as number }));
  } catch {
    return [];
  }
};

const saveStoredItems = (storageKey: string, items: CartItem[]) => {
  const now = Date.now();
  const toStore: StoredCartItem[] = items.map((item) => ({
    listingId: item.listing.id,
    quantity: item.quantity,
    isStoreOwnProduct: item.isStoreOwnProduct || false,
    storeOwnProductId: item.storeOwnProductId,
    isStoreDropshippingProduct: item.isStoreDropshippingProduct || false,
    virtualShopOwnerId: item.virtualShopOwnerId,
    storeProductId: item.storeProductId,
    savedAt: now,
  }));
  if (toStore.length > 0) {
    localStorage.setItem(storageKey, JSON.stringify(toStore));
  } else {
    localStorage.removeItem(storageKey);
  }
};

const getItemSellerId = (item: CartItem): string => {
  if (item.isStoreDropshippingProduct && item.virtualShopOwnerId) {
    return String(item.virtualShopOwnerId);
  }
  return String(item.listing.userId || "");
};

const getIncomingSellerId = (listing: Listing, opts?: AddToCartOpts): string => {
  if (opts?.isStoreDropshippingProduct && opts.virtualShopOwnerId) {
    return String(opts.virtualShopOwnerId);
  }
  if ((listing as any)?.isStoreDropshippingProduct && (listing as any)?.virtualShopOwnerId) {
    return String((listing as any).virtualShopOwnerId);
  }
  return String(listing.userId || "");
};

const getItemKey = (item: {
  listing: Listing;
  isStoreOwnProduct?: boolean;
  storeOwnProductId?: number;
  isStoreDropshippingProduct?: boolean;
  storeProductId?: number;
}) => {
  if (item.isStoreOwnProduct) return `own-${item.storeOwnProductId}`;
  if (item.isStoreDropshippingProduct) return `store-ds-${item.storeProductId}`;
  return `listing-${item.listing.id}`;
};

const getIncomingKey = (params: {
  listingId: number;
  isStoreOwnProduct?: boolean;
  storeOwnProductId?: number;
  isStoreDropshippingProduct?: boolean;
  storeProductId?: number;
}) => {
  if (params.isStoreOwnProduct) return `own-${params.storeOwnProductId}`;
  if (params.isStoreDropshippingProduct) return `store-ds-${params.storeProductId}`;
  return `listing-${params.listingId}`;
};

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [items, setItems] = useState<CartItem[]>([]);
  const [sellerConflict, setSellerConflict] = useState<SellerConflict | null>(null);
  const prevUserIdRef = useRef<string | number | null | undefined>(undefined);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const currentUserId = user?.id || null;
    const prevUserId = prevUserIdRef.current;

    if (prevUserId !== undefined && prevUserId !== currentUserId) {
      if (prevUserId !== null) {
        // User logged out (prevUserId was set, now null) — clear their cart from memory and storage
        if (currentUserId === null) {
          const prevKey = getCartStorageKey(prevUserId);
          localStorage.removeItem(prevKey);
          localStorage.removeItem(GUEST_CART_KEY);
          prevUserIdRef.current = currentUserId;
          setItems([]);
          return;
        }
        // User switched accounts — save previous user's cart then load new user's
        const prevKey = getCartStorageKey(prevUserId);
        saveStoredItems(prevKey, items);
      }
    }
    prevUserIdRef.current = currentUserId;

    // 3-tier account policy: wholesaler / store accounts cannot
    // checkout, so a persisted cart for them is dead state. Wipe
    // storage and skip restore so a leftover cart from a prior role
    // (or a future role downgrade) never lingers on disk.
    if (user && !canBuyAsCustomer(user)) {
      const storageKey = getCartStorageKey(currentUserId);
      localStorage.removeItem(storageKey);
      setItems([]);
      return;
    }

    const storageKey = getCartStorageKey(currentUserId);
    let stored = loadStoredItems(storageKey);

    // Only merge guest cart when transitioning from guest → logged-in on the SAME browser tab session
    // Do NOT merge if the guest cart belongs to a previous user's test session
    if (prevUserId === null && currentUserId !== null) {
      const guestStored = loadStoredItems(GUEST_CART_KEY);
      if (guestStored.length > 0) {
        const existingMap = new Map(stored.map((s) => [getIncomingKey(s), s]));
        for (const g of guestStored) {
          const key = getIncomingKey(g);
          if (existingMap.has(key)) {
            const existing = existingMap.get(key)!;
            existing.quantity += g.quantity;
          } else {
            stored.push(g);
            existingMap.set(key, g);
          }
        }
        localStorage.removeItem(GUEST_CART_KEY);
      }
    }

    if (stored.length === 0) {
      setItems([]);
      return;
    }

    isLoadingRef.current = true;
    Promise.allSettled(
      stored.map(async ({ listingId, quantity, isStoreOwnProduct, storeOwnProductId, isStoreDropshippingProduct, virtualShopOwnerId, storeProductId }) => {
        try {
          if (isStoreOwnProduct && storeOwnProductId) {
            const res = await fetch(`/api/store-product/${storeOwnProductId}?source=own`, { credentials: "include" });
            if (!res.ok) {
              console.error("Failed to fetch cart item:", storeOwnProductId);
              return null;
            }
            const product = await res.json();
            // No longer orderable (turned into a service, «مستعمل» food…
            // the shared cart rule, #176): drop the line.
            if (ownProductCartBlock(product)) return null;
            const listing = {
              id: product.id,
              userId: product.ownerId,
              title: product.title,
              description: product.description || "",
              price: Number(product.price),
              currency: "IQD",
              category: product.category || "",
              subcategory: product.subcategory || "",
              condition: product.condition ?? null,
              imageUrls: product.imageUrls || [],
              isActive: product.isActive,
              quantity: product.quantity,
              listingType: "STORE_OWN",
              createdAt: product.createdAt,
              updatedAt: product.updatedAt,
            } as Listing;
            return { listing, quantity, isStoreOwnProduct: true, storeOwnProductId: product.id };
          }
          if (isStoreDropshippingProduct && storeProductId) {
            const res = await fetch(`/api/store-product/${storeProductId}?source=dropshipping`, { credentials: "include" });
            if (!res.ok) {
              console.error("Failed to fetch cart item:", storeProductId);
              return null;
            }
            const product = await res.json();
            const listing = {
              id: product.listingId,
              // listing.userId MUST be the original wholesaler (merchantId),
              // not the virtual shop owner. The reseller identity is carried
              // separately on the cart item via virtualShopOwnerId.
              userId: product.merchantId || product.seller?.id,
              title: product.title,
              description: product.description || "",
              price: Number(product.price),
              currency: product.currency || "IQD",
              category: product.category || "",
              subcategory: product.subcategory || "",
              imageUrls: product.imageUrls || [],
              isActive: true,
              quantity: product.quantity,
              listingType: "REGULAR",
              createdAt: product.createdAt,
              updatedAt: product.updatedAt,
            } as Listing;
            return {
              listing,
              quantity,
              isStoreOwnProduct: false,
              isStoreDropshippingProduct: true,
              virtualShopOwnerId: virtualShopOwnerId || product.ownerId || product.seller?.id,
              storeProductId: product.id,
            };
          }
          const res = await fetch(`/api/listings/${listingId}`, { credentials: "include" });
          if (!res.ok) {
            console.error("Failed to fetch cart item:", listingId);
            return null;
          }
          const listing = await res.json();
          if ((listing as any)?.redirect === "store-product" && Number((listing as any)?.storeProductId) > 0) {
            const spId = Number((listing as any).storeProductId);
            const storeRes = await fetch(`/api/store-product/${spId}?source=dropshipping`, { credentials: "include" });
            if (!storeRes.ok) {
              console.error("Failed to resolve redirected store product:", spId);
              return null;
            }
            const product = await storeRes.json();
            if (product?.source === "dropshipping" && Number(product?.listingId) > 0) {
              const dsListing = {
                id: product.listingId,
                // Wholesaler owns the underlying listing; merchantId holds their userId.
                userId: product.merchantId || product.seller?.id,
                title: product.title,
                description: product.description || "",
                price: Number(product.price),
                currency: product.currency || "IQD",
                category: product.category || "",
                subcategory: product.subcategory || "",
                imageUrls: product.imageUrls || [],
                isActive: true,
                quantity: product.quantity,
                listingType: "REGULAR",
                createdAt: product.createdAt,
                updatedAt: product.updatedAt,
              } as Listing;
              return {
                listing: dsListing,
                quantity,
                isStoreOwnProduct: false,
                isStoreDropshippingProduct: true,
                virtualShopOwnerId: (listing as any).virtualShopOwnerId || product.ownerId || product.seller?.id,
                storeProductId: product.id,
              };
            }
          }
          return { listing, quantity, isStoreOwnProduct: false, isStoreDropshippingProduct: false };
        } catch (error) {
          console.error("Cart item fetch error:", error);
          return null;
        }
      })
    ).then((results) => {
      const validItems = (results as any)
        .filter((r: any) => r.status === "fulfilled")
        .map((r: any) => r.value)
        .filter((item: any) => item !== null);

      results.forEach((r, index) => {
        if (r.status === "rejected") {
          console.error(`Cart item ${index} failed to load:`, r.reason);
        }
      });

      if (validItems.length > 0) {
        setItems(validItems);
      } else {
        console.warn("All cart items failed to load — keeping previous state");
      }

      isLoadingRef.current = false;
    }).catch((err) => {
      console.error("Cart loading crashed:", err);
      isLoadingRef.current = false;
    });
  }, [user?.id]);

  useEffect(() => {
    if (isLoadingRef.current) return;
    const storageKey = getCartStorageKey(user?.id || null);
    saveStoredItems(storageKey, items);
  }, [items, user?.id]);

  const addToCart = (listing: Listing, opts?: AddToCartOpts) => {
    // Prevent users from buying their own listings/products.
    if (user?.id && listing?.userId && String(user.id) === String(listing.userId)) {
      toast({
        title: "لا يمكنك شراء منتجك الخاص",
        description: "لا يُسمح بإضافة المنتجات المملوكة لك إلى السلة.",
        variant: "destructive",
      });
      return;
    }

    const isVirtualStoreOwnProduct =
      opts?.isStoreOwnProduct === true ||
      (listing as any).isStoreOwnProduct === true ||
      listing.listingType === "STORE_OWN";

    if (isWholesaleMerchantOnly(user) && isVirtualStoreOwnProduct) {
      toast({
        title: "للمشتري والمتاجر فقط",
        description: "هذا المنتج يُباع بالتجزئة ويُضاف للسلة من حساب المشتري أو المتجر الافتراضي فقط.",
        variant: "destructive",
      });
      return;
    }

    // A store service is contact only (the server refuses its orders too).
    if (isVirtualStoreOwnProduct && isStoreServiceCategory(listing.category)) {
      toast({ title: t("listing.storeService"), description: t("listing.serviceContactOnly") });
      return;
    }
    // Same cart rule as the server for every other own product (#176).
    if (isVirtualStoreOwnProduct && ownProductCartBlock(listing as any)) {
      toast({ title: OWN_PRODUCT_NOT_ORDERABLE_MESSAGE, variant: "destructive" });
      return;
    }

    const isOwn = opts?.isStoreOwnProduct || (listing as any).isStoreOwnProduct || false;
    const ownId = opts?.storeOwnProductId || (listing as any).storeOwnProductId || undefined;
    const isStoreDropshipping =
      opts?.isStoreDropshippingProduct ||
      (listing as any).isStoreDropshippingProduct ||
      false;
    const virtualShopOwnerId =
      opts?.virtualShopOwnerId ||
      (listing as any).virtualShopOwnerId ||
      undefined;
    const storeProductId =
      opts?.storeProductId ||
      (listing as any).storeProductId ||
      undefined;

    if (isOwn && !ownId) {
      toast({
        title: "تعذر إضافة المنتج",
        description: "معرف المنتج المباشر مفقود. يرجى تحديث الصفحة والمحاولة مرة أخرى.",
        variant: "destructive",
      });
      return;
    }

    if (isStoreDropshipping && (!storeProductId || !virtualShopOwnerId)) {
      toast({
        title: "تعذر إضافة المنتج",
        description: "بيانات المتجر الافتراضي غير مكتملة. يرجى تحديث الصفحة والمحاولة مرة أخرى.",
        variant: "destructive",
      });
      return;
    }

    // Single-seller enforcement — every item must belong to the same "effective seller"
    if (items.length > 0) {
      const existingSellerId = getItemSellerId(items[0] as any);
      const incomingSellerId = getIncomingSellerId(listing, opts);
      // Trigger conflict if IDs differ, OR if existing item has no identifiable seller
      // (orphaned stale items should not block but should be replaced)
      const shouldConflict =
        existingSellerId !== incomingSellerId ||
        (!existingSellerId && !!incomingSellerId);
      if (shouldConflict) {
        setSellerConflict({ listing, opts });
        return;
      }
    }

    const moq =
      (listing.listingType === "WHOLESALE" && listing.moq && listing.moq > 1 && !isOwn)
        ? listing.moq
        : 1;

    const itemKey = getIncomingKey({
      listingId: listing.id,
      isStoreOwnProduct: isOwn,
      storeOwnProductId: ownId,
      isStoreDropshippingProduct: isStoreDropshipping,
      storeProductId,
    });

    setItems((prev) => {
      const existing = prev.find((item) => {
        const existKey = getItemKey(item);
        return existKey === itemKey;
      });
      if (existing) {
        return prev.map((item) => {
          const ik = getItemKey(item);
          if (ik !== itemKey) return item;
          // Same stock guard as updateQuantity. If a + click would push
          // the line over the seller's available quantity, hold at the
          // ceiling and surface a toast.
          const stock =
            typeof item.listing.quantity === "number" && item.listing.quantity > 0
              ? item.listing.quantity
              : MAX_QTY;
          if (item.quantity >= stock) {
            toast({
              title: t("cart.maxStockTitle"),
              description: `${t("cart.maxStockDescPrefix")} ${stock} ${t("cart.maxStockDescSuffix")}`,
              variant: "destructive",
            });
            return item;
          }
          return { ...item, quantity: Math.min(item.quantity + 1, stock) };
        });
      }
      if (prev.length >= MAX_CART_ITEMS) {
        toast({
          title: "السلة ممتلئة",
          description: `لا يمكن إضافة أكثر من ${MAX_CART_ITEMS} منتج في السلة`,
          variant: "destructive",
        });
        return prev;
      }
      return [...prev, {
        listing,
        quantity: moq,
        isStoreOwnProduct: isOwn,
        storeOwnProductId: ownId,
        isStoreDropshippingProduct: isStoreDropshipping,
        virtualShopOwnerId,
        storeProductId,
      }];
    });
  };

  const removeFromCart = (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => {
    const targetKey = getIncomingKey({
      listingId,
      isStoreOwnProduct: opts?.isStoreOwnProduct,
      storeOwnProductId: opts?.storeOwnProductId,
      isStoreDropshippingProduct: opts?.isStoreDropshippingProduct,
      storeProductId: opts?.storeProductId,
    });
    setItems((prev) => prev.filter((item) => getItemKey(item) !== targetKey));
  };

  const updateQuantity = (
    listingId: number,
    quantity: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => {
    const targetKey = getIncomingKey({
      listingId,
      isStoreOwnProduct: opts?.isStoreOwnProduct,
      storeOwnProductId: opts?.storeOwnProductId,
      isStoreDropshippingProduct: opts?.isStoreDropshippingProduct,
      storeProductId: opts?.storeProductId,
    });

    if (quantity <= 0) {
      removeFromCart(listingId, opts);
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        if (getItemKey(item) !== targetKey) return item;
        const moq =
          (item.listing.listingType === "WHOLESALE" &&
            item.listing.moq &&
            item.listing.moq > 1 &&
            !item.isStoreOwnProduct)
            ? item.listing.moq
            : 1;
        // Clamp to the seller's actual stock if the listing carries a
        // finite quantity. `listing.quantity` is populated by storage.ts
        // for both regular listings, STORE_OWN products, and reseller-
        // inventory rows; a null/undefined value means "open quantity"
        // (services, custom orders) in which case we keep the original
        // hard cap. Surfaces a toast so the buyer understands why their
        // requested amount didn't go up — previously the cart silently
        // capped at MAX_QTY=99, which let buyers commit to quantities
        // the seller couldn't fulfil.
        const stock =
          typeof item.listing.quantity === "number" && item.listing.quantity > 0
            ? item.listing.quantity
            : MAX_QTY;
        const safeQty = Math.min(Math.max(quantity, moq), Math.min(stock, MAX_QTY));
        if (quantity > stock && quantity > item.quantity) {
          toast({
            title: t("cart.maxStockTitle"),
            description: `${t("cart.maxStockDescPrefix")} ${stock} ${t("cart.maxStockDescSuffix")}`,
            variant: "destructive",
          });
        }
        return { ...item, quantity: safeQty };
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const resolveConflict = (replace: boolean) => {
    if (!sellerConflict) return;
    if (replace) {
      const { listing, opts } = sellerConflict;
      const isOwn = opts?.isStoreOwnProduct || false;
      const ownId = opts?.storeOwnProductId;
      const isStoreDropshipping = opts?.isStoreDropshippingProduct || false;
      const vso = opts?.virtualShopOwnerId;
      const spId = opts?.storeProductId;
      const moq =
        (listing.listingType === "WHOLESALE" && listing.moq && listing.moq > 1 && !isOwn)
          ? listing.moq
          : 1;
      setItems([{
        listing,
        quantity: moq,
        isStoreOwnProduct: isOwn,
        storeOwnProductId: ownId,
        isStoreDropshippingProduct: isStoreDropshipping,
        virtualShopOwnerId: vso,
        storeProductId: spId,
      }]);
    }
    setSellerConflict(null);
  };

  const isInCart = (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => {
    const targetKey = getIncomingKey({
      listingId,
      isStoreOwnProduct: opts?.isStoreOwnProduct,
      storeOwnProductId: opts?.storeOwnProductId,
      isStoreDropshippingProduct: opts?.isStoreDropshippingProduct,
      storeProductId: opts?.storeProductId,
    });
    return items.some((item) => getItemKey(item) === targetKey);
  };

  const getQuantity = (
    listingId: number,
    opts?: {
      isStoreOwnProduct?: boolean;
      storeOwnProductId?: number;
      isStoreDropshippingProduct?: boolean;
      storeProductId?: number;
    }
  ) => {
    const targetKey = getIncomingKey({
      listingId,
      isStoreOwnProduct: opts?.isStoreOwnProduct,
      storeOwnProductId: opts?.storeOwnProductId,
      isStoreDropshippingProduct: opts?.isStoreDropshippingProduct,
      storeProductId: opts?.storeProductId,
    });
    return items.find((item) => getItemKey(item) === targetKey)?.quantity || 0;
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPriceIQD = items
    .filter((item) => (item.listing.currency ?? "IQD") === "IQD")
    .reduce((sum, item) => sum + (item.listing.price || 0) * item.quantity, 0);

  const totalPriceUSD = items
    .filter((item) => item.listing.currency === "USD")
    .reduce((sum, item) => sum + (item.listing.price || 0) * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        sellerConflict,
        resolveConflict,
        removeFromCart,
        updateQuantity,
        clearCart,
        isInCart,
        getQuantity,
        totalItems,
        totalPriceIQD,
        totalPriceUSD,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
